import { useState, useEffect, type RefObject } from 'react';

export interface IElementDimensions {
  /** On-screen width, including any ancestor CSS scale */
  width: number | null;
  left: number | null;
  /** Layout width (`offsetWidth`), which CSS transforms do not change */
  layoutWidth: number | null;
}

/**
 * Hook to track an element's dimensions using ResizeObserver
 * @param elementRef - React ref to the element to observe
 * @returns Object containing width and left position
 */
export function useElementDimensions(
  elementRef: RefObject<HTMLElement | null>,
): IElementDimensions {
  const [dimensions, setDimensions] = useState<IElementDimensions>({
    width: null,
    left: null,
    layoutWidth: null,
  });

  // eslint-disable-next-line no-restricted-syntax -- reason: ResizeObserver requires imperative API setup and cleanup
  useEffect(() => {
    const element = elementRef?.current;
    if (!element) return;

    const updateDimensions = (target: Element) => {
      const { width, left } = target.getBoundingClientRect();
      const layoutWidth =
        target instanceof HTMLElement ? target.offsetWidth : width;
      setDimensions(prev =>
        prev.width === width &&
        prev.left === left &&
        prev.layoutWidth === layoutWidth
          ? prev
          : { width, left, layoutWidth },
      );
    };

    const resizeObserver = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        updateDimensions(entry.target);
      }
    });

    resizeObserver.observe(element);

    // Initial measurement
    updateDimensions(element);

    return () => {
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reason: elementRef.current is the dependency that changes
  }, [elementRef?.current]);

  return dimensions;
}
